import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../../i18n'
import { getStandardDisplayName } from '../../../utils/complianceStandard'
import { executiveGovernanceStandardPath } from '../../../services/executiveGovernanceApi'
import type { PostureMatrixRow, PostureReadinessStatus, RuntimeDriftLevel } from '../../../services/executivePostureApi'
import entStyles from '../../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import widgetStyles from '../../../styles/pages/home/ExecutivePostureWidgets.module.scss'

type Props = {
  rows: PostureMatrixRow[]
  loading?: boolean
}

function readinessClass(status: PostureReadinessStatus): string {
  switch (status) {
    case 'READY':
      return entStyles.badgeStable
    case 'IN_PROGRESS':
      return widgetStyles.readinessInProgress
    case 'BLOCKED':
      return entStyles.badgeRisk
    default:
      return widgetStyles.readinessNotStarted
  }
}

function driftClass(level: RuntimeDriftLevel): string {
  if (level === 'HIGH') return entStyles.badgeRisk
  if (level === 'MEDIUM') return entStyles.badgeWarn
  return entStyles.badgePressureLow
}

function readinessLabel(t: ReturnType<typeof useI18n>['t'], status: PostureReadinessStatus): string {
  const key = `home.executivePosture.readiness.${status}`
  const defaults: Record<PostureReadinessStatus, string> = {
    READY: 'Ready',
    IN_PROGRESS: 'In progress',
    BLOCKED: 'Blocked',
    NOT_STARTED: 'Not started',
  }
  return t(key, defaults[status])
}

function readinessTooltip(
  t: ReturnType<typeof useI18n>['t'],
  status: PostureReadinessStatus,
): string | undefined {
  if (status === 'BLOCKED') {
    return t(
      'home.executivePosture.readiness.blockedTooltip',
      'Blocked means audit-ready status is prevented by evidence gaps, pending verification, critical findings, or overdue remediation.',
    )
  }
  if (status === 'READY') {
    return t(
      'home.executivePosture.readiness.readyTooltip',
      'Ready means all scoped controls are completed, verified, and have no blocking gaps.',
    )
  }
  return undefined
}

function formatReadinessCell(row: PostureMatrixRow, t: ReturnType<typeof useI18n>['t']): string {
  const label = readinessLabel(t, row.readinessStatus)
  if (row.readinessPercentage != null && row.readinessStatus !== 'NOT_STARTED') {
    return `${label} · ${row.readinessPercentage}%`
  }
  return label
}

export default function CompliancePostureMatrix({ rows, loading }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()

  return (
    <div className={entStyles.posturePanel} data-testid="compliance-posture-matrix">
      <h2 className={entStyles.postureTitle}>
        {t('home.executivePosture.matrix.title', 'Compliance Posture Matrix')}
      </h2>
      <p className={entStyles.postureSubtitle}>
        {t(
          'home.executivePosture.matrix.subtitle',
          'Operational governance posture by compliance framework.',
        )}
      </p>

      {loading ? (
        <p className={entStyles.emptyState}>{t('common.loading', 'Loading…')}</p>
      ) : !rows.length ? (
        <p className={entStyles.emptyState}>
          {t('home.executivePosture.matrix.empty', 'No standards in scope yet.')}
        </p>
      ) : (
        <div className={widgetStyles.matrixTable} role="table">
          <div className={widgetStyles.matrixHead} role="row">
            <span role="columnheader">{t('home.executivePosture.matrix.colStandard', 'Standard')}</span>
            <span role="columnheader">{t('home.executivePosture.matrix.colSelf', 'Self-Assessed')}</span>
            <span role="columnheader">{t('home.executivePosture.matrix.colVerified', 'Verified')}</span>
            <span role="columnheader">
              {t('home.executivePosture.matrix.colReadiness', 'Audit Readiness')}
            </span>
            <span role="columnheader">{t('home.executivePosture.matrix.colGaps', 'Open Gaps')}</span>
            <span role="columnheader">{t('home.executivePosture.matrix.colDrift', 'Runtime Drift')}</span>
          </div>
          <ul className={widgetStyles.matrixBody}>
            {rows.map((row) => {
              const name = row.standardTitle || getStandardDisplayName(t, row.standardCode)
              const readinessTip = readinessTooltip(t, row.readinessStatus)
              return (
                <li key={row.standardCode}>
                  <button
                    type="button"
                    className={widgetStyles.matrixRow}
                    role="row"
                    onClick={() => navigate(executiveGovernanceStandardPath(row.standardCode))}
                  >
                    <span className={widgetStyles.matrixCellStandard} role="cell">
                      <span className={entStyles.standardChip}>{name}</span>
                      {row.compactBlockReason ? (
                        <span className={widgetStyles.matrixBlockReason}>{row.compactBlockReason}</span>
                      ) : null}
                      {row.blockReasons?.length ? (
                        <ul className={widgetStyles.matrixBlockReasonList} aria-label="Blocker details">
                          {row.blockReasons.map((reason) => (
                            <li key={reason.code}>{reason.label}</li>
                          ))}
                        </ul>
                      ) : null}
                    </span>
                    <span className={widgetStyles.matrixCellMetric} role="cell">
                      {row.selfAssessedPercentage}%
                    </span>
                    <span className={widgetStyles.matrixCellMetric} role="cell">
                      {row.verifiedPercentage != null
                        ? `${row.verifiedPercentage}%`
                        : t('home.executivePosture.notVerified', 'Not verified')}
                    </span>
                    <span
                      className={widgetStyles.matrixCellReadiness}
                      role="cell"
                      title={readinessTip}
                    >
                      <span className={readinessClass(row.readinessStatus)}>
                        {formatReadinessCell(row, t)}
                      </span>
                    </span>
                    <span className={widgetStyles.matrixCellMetric} role="cell">
                      {row.openGaps}
                    </span>
                    <span className={widgetStyles.matrixCellBadge} role="cell">
                      <span className={driftClass(row.runtimeDrift)}>{row.runtimeDrift}</span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
